/* eslint-disable max-lines-per-function */
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/anlageassistent/entities/Arbeitsbereich';
import { useArbeitsbereiche } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useArbeitsbereiche/useArbeitsbereiche';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { faAnglesUpDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function ArbeitsbereichSelect({
  value,
  handleChange,
}: {
  value: string;
  handleChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();
  const { arbeitsbereiche } = useArbeitsbereiche(currentStandort.id);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState('');

  function onChangeFilter(value: string) {
    setFilter(value);
  }

  const selectedArbeitsbereich = arbeitsbereiche.find((ab) => ab.id === value);
  return (
    <div>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger className="hidden md:flex" asChild>
          <Button
            className="md:flex flex-row justify-between gap-1 w-full text-neutrals-900 text-sm font-normal hidden border-neutrals-300"
            variant="outline-secondary"
            role="combobox">
            <span className="truncate">
              {selectedArbeitsbereich ? (
                <ArbeitsbereichItem arbeitsbereich={selectedArbeitsbereich} />
              ) : (
                <span className="text-neutrals-700">
                  {t(
                    `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.placeholder`,
                  )}
                </span>
              )}
            </span>
            <FontAwesomeIcon icon={faAnglesUpDown} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={`bg-white p-2 px-0 w-108 hidden md:flex`}>
          <Command>
            <CommandInput
              placeholder={t(
                `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.filterPlaceholder`,
              )}
            />
            <CommandList>
              {arbeitsbereiche.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => {
                    handleChange(option.id);
                    setIsPopoverOpen(false);
                  }}>
                  <ArbeitsbereichItem
                    arbeitsbereich={option}
                    selected={option.id === selectedArbeitsbereich?.id}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Button
            className="flex flex-row justify-between gap-1 w-full text-neutrals-900 text-sm font-normal md:hidden"
            variant="outline-secondary"
            role="combobox">
            <span className="truncate">
              {selectedArbeitsbereich ? (
                <ArbeitsbereichItem arbeitsbereich={selectedArbeitsbereich} />
              ) : (
                t(
                  `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.placeholder`,
                )
              )}
            </span>
            <FontAwesomeIcon icon={faAnglesUpDown} />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t(
              `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.label`,
            )}
          </div>
          <Input
            className="grow"
            defaultValue={filter}
            placeholder={t(
              `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.filterPlaceholder`,
            )}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2">
              {arbeitsbereiche
                .filter((o) =>
                  o.name
                    .toLocaleLowerCase()
                    .includes(filter.toLocaleLowerCase()),
                )
                .map((option) => (
                  <div
                    className="flex flex-col gap-2 not-last:border-b border-neutrals-200 pb-2"
                    key={option.id}
                    onClick={() => {
                      handleChange(option.id);
                      setIsDrawerOpen(false);
                    }}>
                    <ArbeitsbereichItem
                      arbeitsbereich={option}
                      selected={option.id === selectedArbeitsbereich?.id}
                    />
                  </div>
                ))}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function ArbeitsbereichItem({
  arbeitsbereich,
  selected,
}: {
  arbeitsbereich: Arbeitsbereich;
  selected?: boolean;
}) {
  return (
    <div className="flex flex-row gap-2 items-center">
      <FontAwesomeIcon
        icon={faCircle}
        color={arbeitsbereich.getBorderColor()}
      />
      <span className="text-neutrals-900 text-sm font-normal">
        {arbeitsbereich.name}
      </span>
      {selected && (
        <FontAwesomeIcon
          icon={faCheck}
          className="text-primary-blue-600 ml-auto"
        />
      )}
    </div>
  );
}
